import { computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import {
  hasEmsV3Access,
  TradeWorkflowSpec,
  TradeWorkflowSpecUpdateParams,
} from '@pinnakl/poems/trade-workflow-spec/domain';
import { withDevtools } from '@pinnakl/shared/signal-store';
import { isNeitherNullNorUndefined } from '@pinnakl/shared/util-helpers';
import { filter } from 'rxjs';
import { TradeWorkflowSpecApiService } from './trade-workflow-spec-api.service';

interface TradeWorkflowSpecStoreState {
  spec: TradeWorkflowSpec | null;
  loading: boolean;
  loaded: boolean;
}

const initialState: TradeWorkflowSpecStoreState = {
  spec: null,
  loading: false,
  loaded: false,
};

export const TradeWorkflowSpecStore = signalStore(
  { providedIn: 'root' },
  withDevtools('tradeWorkflowSpec'),
  withState(initialState),
  withComputed(store => ({
    hasEmsV3Access: computed(() => {
      const spec = store.spec();
      return spec ? hasEmsV3Access(spec) : false;
    }),
  })),
  withMethods((store, api = inject(TradeWorkflowSpecApiService)) => {
    // Single in-flight request so concurrent callers (eager app load + route guards) share one fetch.
    let inFlight: Promise<TradeWorkflowSpec> | null = null;

    const fetchSpec = (): Promise<TradeWorkflowSpec> => {
      patchState(store, { loading: true, loaded: false });
      inFlight = api
        .getTradeWorkflowSpec()
        .then(spec => {
          patchState(store, { spec, loading: false, loaded: true });
          return spec;
        })
        .catch(error => {
          patchState(store, { loading: false });
          throw error;
        })
        .finally(() => (inFlight = null));
      return inFlight;
    };

    return {
      loadTradeWorkflowSpec: (): Promise<TradeWorkflowSpec> => inFlight ?? fetchSpec(),

      // Resolves with the spec, loading it first when it isn't in the store yet (route-guard gate).
      ensureLoaded: (): Promise<TradeWorkflowSpec> => {
        const spec = store.spec();
        return store.loaded() && spec ? Promise.resolve(spec) : (inFlight ?? fetchSpec());
      },

      updateTradeWorkflowSpec: async (params: TradeWorkflowSpecUpdateParams): Promise<void> => {
        const current = store.spec();
        const updated = await (current
          ? api.updateTradeWorkflowSpec({ ...params, id: current.id })
          : api.createTradeWorkflowSpec(params));
        patchState(store, state => ({ spec: { ...state.spec, ...updated } }));
      },
    };
  }),
  withProps(store => ({
    // Continuous non-null stream for RxJS consumers; mirrors the legacy facade's `spec$`.
    spec$: toObservable(store.spec).pipe(filter(isNeitherNullNorUndefined)),
  })),
);
